// src/lib/auth.js
import axios from "axios";
import { loadAuth, clearAuth, saveAuth } from "./authMgmt";
import { getSubdomain } from "./helper";

export const API_URL = process.env.REACT_APP_API_URL;
export const DOMAIN_NAME = process.env.REACT_APP_DOMAIN_NAME;

export const IS_DEV = process.env.NODE_ENV === "development";

// ---------- jwt parsing ----------
function decodeJwt(token) {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}
function secondsUntilExpiry(token) {
  const p = decodeJwt(token);
  if (!p?.exp) return 0;
  return p.exp - Math.floor(Date.now() / 1000);
}

// ---------- refresh machinery ----------
let refreshPromise = null; // shared in-flight refresh

async function refreshAccessToken() {
  // if a refresh is already happening, reuse it
  if (refreshPromise) return refreshPromise;

  // Use a standalone axios just for refresh (cookie-based)
  const client = axios.create({
    baseURL: API_URL,
    withCredentials: true, // send/receive the HttpOnly refresh cookie
    headers: { "Content-Type": "application/json" },
  });

  refreshPromise = client
    .post("/api/auth/refresh", {})
    .then((res) => {
      const { accessToken } = res.data || {};
      if (!accessToken) throw new Error("No accessToken from refresh");
      const prevAuth = loadAuth();
      saveAuth({
        token: accessToken,
        role: prevAuth?.role ?? null,
        user: prevAuth?.user ?? null,
        company: prevAuth?.company ?? null,
      }); // persist new token
      return accessToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// ---------- main API factory ----------
export function getApiClient({
  baseURL = API_URL,
  onLogout = () => {
    clearAuth();
    if (getSubdomain() === "") {
      window.location.href = "/";
    } else {
      window.location.href = `https://${getSubdomain()}.${DOMAIN_NAME}/login`;
    }
  },
  skewSeconds = 60,
} = {}) {
  const api = axios.create({
    baseURL,
    headers: { "Content-Type": "application/json" },
    paramsSerializer: (params) => {
      // Add a timestamp to all requests to bust CORS preflight cache
      return new URLSearchParams({
        ...params,
        _: Date.now().toString(), // Cache-busting query parameter
      }).toString();
    },
  });

  // Add a request interceptor to append a cache-busting parameter
  api.interceptors.request.use((config) => {
    // Append a timestamp to the URL to bust the CORS cache
    const separator = config.url.includes("?") ? "&" : "?";
    config.url += `${separator}_=${Date.now()}`;

    // Attach token/tenant + preemptive refresh (your existing logic)
    const { token, tenant } = loadAuth();
    if (token) {
      const left = secondsUntilExpiry(token);
      if (left <= skewSeconds) {
        return refreshAccessToken()
          .then((newToken) => {
            config.headers.Authorization = `Bearer ${newToken}`;
            return config;
          })
          .catch((e) => {
            onLogout();
            return Promise.reject(
              new axios.Cancel("Refresh failed; logging out")
            );
          });
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    if (tenant) config.headers["X-Tenant"] = tenant;
    return config;
  });

  // on 401 → try one refresh then retry request
  api.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config || {};
      const status = error?.response?.status;

      // if unauthorized, and we haven't retried yet
      if (status === 401 && !original._retry) {
        original._retry = true;
        try {
          const newToken = await refreshAccessToken();
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original); // retry
        } catch (e) {
          onLogout();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );

  return api;
}
